// ** React Imports
import { useState, Fragment } from 'react'

// ** Next Import
import { useNavigate } from 'react-router-dom'

// ** MUI Imports
import Box from '@mui/material/Box'
import Menu from '@mui/material/Menu'
import Badge from '@mui/material/Badge'
import Avatar from '@mui/material/Avatar'
import Divider from '@mui/material/Divider'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'

// ** Icons Imports
import CogOutline from 'mdi-material-ui/CogOutline'
import CurrencyUsd from 'mdi-material-ui/CurrencyUsd'
import EmailOutline from 'mdi-material-ui/EmailOutline'
import LogoutVariant from 'mdi-material-ui/LogoutVariant'
import AccountOutline from 'mdi-material-ui/AccountOutline'
import MessageOutline from 'mdi-material-ui/MessageOutline'
import HelpCircleOutline from 'mdi-material-ui/HelpCircleOutline'
import UserIcon from '../../../../assets/images/user.png'
import PermIdentityIcon from '@mui/icons-material/PermIdentity'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
// ** Context
import { useAuth } from 'src/hooks/useAuth'
import Translations from 'src/layouts/components/Translations'
import MyProfileIcon from 'src/@core/components/icons/MyProfileIcon'
import BackToOujatIcon from 'src/@core/components/icons/BackToOujatIcon'
import HelpIcon from 'src/@core/components/icons/HelpIcon'
import NotificationsIcon from '@mui/icons-material/Notifications'
import { useTranslation } from 'react-i18next'
import { ApiEndPoints, MEDIA_URL } from 'src/network/endpoints'
import { axiosInstance } from 'src/network/adapter'
import { toastError, toastSuccess } from 'src/utils/utils'
// ** Styled Components
const BadgeContentSpan = styled('span')(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  boxShadow: `0 0 0 2px ${theme.palette.background.paper}`
}))

const menuItemStyles = {
  py: 2,
  px: 4,
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    fontSize: '1.25rem',
    mr: 3,
    color: 'text.secondary'
  }
}

const activeMenuItemStyles = {
  ...menuItemStyles,
  '& .MuiTypography-root': {
    fontWeight: 600,
    color: 'text.primary'
  }
}

const inactiveMenuItemStyles = {
  ...menuItemStyles,
  borderLeft: '3px solid transparent',
  '& .MuiTypography-root': {
    color: 'text.secondary'
  }
}

const UserDropdown = props => {
  // ** Props
  const { settings } = props

  // ** States
  const [anchorEl, setAnchorEl] = useState(null)
  const [loadingHelp, setLoadingHelp] = useState(false)
  const isMenuOpen = Boolean(anchorEl)
  // ** Hooks
  const navigate = useNavigate()
  const { logout, user } = useAuth()
  const { t } = useTranslation()

  // ** Vars
  const { direction } = settings

  const handleDropdownOpen = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleDropdownClose = url => {
    if (url) {
      navigate(url)
    }
    setAnchorEl(null)
  }

  // Handle Help menu item click - fetch conversation and navigate
  const handleHelpClick = async () => {
    setLoadingHelp(true)
    setAnchorEl(null) // Close dropdown immediately

    try {
      // Call API to get or create admin-user conversation
      // Using 'customerAdminChat' as groupType for customer-admin chat
      const response = await axiosInstance.post(ApiEndPoints.Chat.adminUserConversation('customerAdminChat'))

      if (response?.data?.status === 'success' && response?.data?.data?.conversation) {
        const conversation = response.data.data.conversation

        // Navigate to help chat page with conversation data
        navigate('/help', {
          state: {
            conversationId: conversation._id,
            conversation: conversation
          }
        })
      } else {
        toastError(t('Profile Section.toast.helpChatFailed'))
      }
    } catch (error) {
      console.error('Error fetching admin conversation:', error)
      toastError(error?.response?.data?.message || t('Profile Section.toast.helpChatFailed'))
      // Still navigate to help page even if API fails
      navigate('/help')
    } finally {
      setLoadingHelp(false)
    }
  }
  const styles = {
    py: 2,
    px: 4,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    color: 'text.primary',
    textDecoration: 'none',
    '& svg': {
      fontSize: '1.375rem',
      color: 'text.secondary'
    }
  }

  const handleLogout = () => {
    logout()
    handleDropdownClose()
  }

  // Handle navigation and close dropdown
  const handleNavigation = url => {
    setAnchorEl(null)
    if (url) {
      setTimeout(() => {
        navigate(url)
      }, 0)
    }
  }

  return (
    <Fragment>
      <Box
        sx={{
          height: '48px',
          width: '80px',
          border: '1px solid #E7E9ED',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          cursor: 'pointer',
          px: 2
        }}
        onClick={handleDropdownOpen}
      >
        <Badge overlap='circular' sx={{ cursor: 'pointer' }}>
          <Avatar
            alt={`${user?.firstName || ''} ${user?.lastName || ''}`}
            onClick={handleDropdownOpen}
            sx={{ width: 32, height: 32 }}
            src={user?.profileImg ? `${MEDIA_URL}${user.profileImg}` : UserIcon}
          />
        </Badge>
        {isMenuOpen ? (
          <ExpandLessIcon sx={{ color: '#94A9B4', cursor: 'pointer', fontSize: '30px' }} />
        ) : (
          <ExpandMoreIcon sx={{ color: '#94A9B4', cursor: 'pointer', fontSize: '30px' }} />
        )}{' '}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleDropdownClose()}
        sx={{ '& .MuiMenu-paper': { width: 230, mt: 4 } }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: direction === 'ltr' ? 'right' : 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: direction === 'ltr' ? 'right' : 'left'
        }}
      >
        <Box sx={{ pt: 2, pb: 3, px: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Badge
              overlap='circular'
              badgeContent={<BadgeContentSpan />}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
            >
              <Avatar
                alt={user?.firstName || 'User'}
                src={user?.profileImg ? `${MEDIA_URL}${user.profileImg}` : UserIcon}
                sx={{ width: '40px', height: '40px' }}
              />
            </Badge>
            <Box sx={{ display: 'flex', ml: 3, alignItems: 'flex-start', flexDirection: 'column' }}>
              <Typography sx={{ fontWeight: 600 }}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant='body2' sx={{ fontSize: '13px', color: '#8F9DAC !important', fontWeight: 700 }}>
                {user?.role}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ mt: 0, mb: 1, color: '#C0C0C0' }} />
        {/* <MenuItem
          sx={activeMenuItemStyles}
          onClick={() => handleDropdownClose('/dashboard')}
        >
          <MyProfileIcon />
          <Typography variant='fm-p1' sx={{ fontWeight: 700, color: '#8F9DAC !important' }}>{t('Profile Section.My Profile')}</Typography>
        </MenuItem> */}
        {/* <MenuItem
          sx={inactiveMenuItemStyles}
          onClick={() => {
            navigate('/update-profile')
            handleDropdownClose()
          }}
        >
          <MyProfileIcon />
          <Typography variant='fm-p1' sx={{ fontWeight: 700, color: '#8F9DAC !important' }}>
            {t('Profile Section.Update Profile')}
          </Typography>
        </MenuItem> */}
        <MenuItem
          sx={inactiveMenuItemStyles}
          onClick={() => {
            navigate('/profile')
            handleDropdownClose()
          }}
        >
          <MyProfileIcon />
          <Typography variant='fm-p1' sx={{ fontWeight: 700, color: '#8F9DAC !important' }}>
            {t('Profile Section.My Profile')}
          </Typography>
        </MenuItem>
        <MenuItem sx={inactiveMenuItemStyles} onClick={() => handleDropdownClose('/dashboard')}>
          <Box>
            <BackToOujatIcon />
          </Box>
          <Typography variant='fm-p1' sx={{ fontWeight: 700, color: '#8F9DAC !important' }}>
            {t('Profile Section.Go to')}{' '}
            <Box component='span' sx={{ color: '#FF5722' }}>
              {t('Profile Section.Oujat.com')}
            </Box>
          </Typography>
        </MenuItem>
        <MenuItem sx={inactiveMenuItemStyles} onClick={handleHelpClick} disabled={loadingHelp}>
          <HelpIcon />
          <Typography variant='fm-p1' sx={{ fontWeight: 700, color: '#8F9DAC !important' }}>
            {loadingHelp ? t('common.loading') : t('Profile Section.Help')}
          </Typography>
        </MenuItem>
        {/* <MenuItem
          sx={inactiveMenuItemStyles}
          onClick={() => handleDropdownClose('/notification')}
        >
          <NotificationsIcon />
          <Typography variant='fm-p1' sx={{ fontWeight: 700, color: '#8F9DAC !important' }}>{t('Profile Section.notification')}</Typography>
        </MenuItem> */}
        <MenuItem onClick={handleLogout} sx={inactiveMenuItemStyles}>
          <LogoutOutlinedIcon sx={{ fontSize: 24, color: '#8F9DAC !important' }} />
          <Typography variant='fm-p1' sx={{ fontWeight: 700, color: '#8F9DAC !important' }}>
            {t('Profile Section.Logout')}
          </Typography>
        </MenuItem>
      </Menu>
    </Fragment>
  )
}

export default UserDropdown
